import { Badge, Button, Group, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Bdm1Outcome, NachversandKind } from '@prisma/client';
import type {
  Bdm2OutcomeResDto,
  ParticipantBdmStateResDto,
  RetrievalResDto,
} from '@web/common';
import {
  MODE_KEY,
  OUTCOME_KEY,
  bdm1SwitchEligibility,
  fmtDate,
  type ActionKind,
  type Bdm1SwitchEligibility,
} from './BdmRetrievalsSection.helpers';
import { ActionButton, StatusCell, TitledTable } from './RetrievalTableParts';

// Shown beside the action buttons, not just on hover: it explains the retrieval's own state
// (already picked up, deadline overdue), which is relevant context for both of them, not only
// for the switch-to-voucher button. '' when there is nothing to say.
const switchNoteLabel = (
  t: (key: string, values?: Record<string, unknown>) => string,
  { blockReason, daysOverdue }: Bdm1SwitchEligibility,
): string => {
  if (blockReason === 'notEligible') {
    return t('Panel.SwitchToVoucherBlocked.NotEligible');
  }
  if (blockReason === 'alreadyPickedUp') {
    return t('Panel.SwitchToVoucherBlocked.AlreadyPickedUp');
  }
  if (daysOverdue !== null) {
    return t('Panel.SwitchToVoucherBlocked.DeadlinePassed', {
      days: daysOverdue,
    });
  }
  return '';
};

// GROUP_1 macht beide Runden → BDM 1 + 2 immer zeigen.
const ROUNDS = [NachversandKind.BDM1, NachversandKind.BDM2];

// Produkt / Gutschein / Bargeld des BDM2-Outcomes; jede Zeile hängt nur an ihren Daten.
// Das Produkt an der Variante, nicht an `drawnProduct`: das ist das gezogene Produkt und
// auch bei `noProduct` gefüllt.
const Bdm2OutcomeCell = ({ outcome }: { outcome: Bdm2OutcomeResDto }) => {
  const { t } = useTranslation('nachversand');

  return (
    <>
      {outcome.variant !== 'noProduct' && (
        <Group gap="6px">
          <Badge variant="light" color="gray">
            {t('Panel.Bdm2Result.Product')}
          </Badge>
          <Text size="14px">{outcome.drawnProduct}</Text>
        </Group>
      )}
      {outcome.galaxusValue > 0 && (
        <Group gap="6px">
          <Badge variant="light" color="primary">
            {t('Panel.Bdm2Result.Voucher')}
          </Badge>
          <Text size="14px">{`${outcome.galaxusValue} CHF`}</Text>
        </Group>
      )}
      {outcome.cashMoney > 0 && (
        <Group gap="6px">
          <Badge variant="light" color="warningAmber">
            {t('Panel.Bdm2Result.Cash')}
          </Badge>
          <Text size="14px">{`${outcome.cashMoney} CHF`}</Text>
        </Group>
      )}
    </>
  );
};

type Props = {
  retrievals: RetrievalResDto[];
  bdmState: ParticipantBdmStateResDto | null;
  onAction: (retrieval: RetrievalResDto, kind: ActionKind) => void;
  onSwitchToVoucher: () => void;
};

export const BdmTable = ({
  retrievals,
  bdmState,
  onAction,
  onSwitchToVoucher,
}: Props) => {
  const { t } = useTranslation('nachversand');

  // Ergebnis-Spalte: Ausfüllstatus + Outcome aus bdm-state — BDM1 als Text,
  // BDM2 als Produkt-/Gutschein-/Bargeld-Zeilen.
  const ergebnis = (
    kind: NachversandKind,
    retrieval: RetrievalResDto | undefined,
  ) => {
    const isBdm1 = kind === NachversandKind.BDM1;
    const survey = isBdm1 ? bdmState?.bdm1Survey : bdmState?.bdm2Survey;
    const bdm2Outcome = isBdm1 ? null : bdmState?.bdm2Outcome;

    // Ein importierter Outcome beweist das Ausfüllen selbst — die Fillout-Erkennung
    // ist fensterbegrenzt und kann kein Datum gesetzt haben.
    if (!survey?.filledOutAt && !bdm2Outcome) {
      return (
        <Text size="14px" c="dimmed" fs="italic">
          {t('Panel.Result.NotFilledOut')}
        </Text>
      );
    }

    const bdm1Outcome =
      isBdm1 && bdmState?.bdm1Outcome
        ? t(OUTCOME_KEY[bdmState.bdm1Outcome])
        : null;

    // The outcome-detection job never sets an actor, so a VOUCHER outcome with one can only
    // come from the sample-to-voucher switch, not from the participant's own survey choice.
    const switchedManually =
      isBdm1 &&
      bdmState?.bdm1Outcome === Bdm1Outcome.VOUCHER &&
      retrieval?.actorUserId != null;

    return (
      <Stack gap="4px">
        {bdm1Outcome && <Text size="14px">{bdm1Outcome}</Text>}
        {bdm2Outcome && <Bdm2OutcomeCell outcome={bdm2Outcome} />}
        {survey?.filledOutAt && (
          <Text size="12px" c="dimmed">
            {t('Panel.Result.FilledOut', { date: fmtDate(survey.filledOutAt) })}
          </Text>
        )}
        {switchedManually && retrieval && (
          <Text size="12px" c="dimmed">
            {t('Panel.Result.SwitchedToVoucher', {
              date: fmtDate(retrieval.updatedAt),
            })}
          </Text>
        )}
      </Stack>
    );
  };

  return (
    <TitledTable
      title={t('Panel.Section.Bdm')}
      columns={[
        t('Panel.Columns.Round'),
        t('Panel.Columns.Result'),
        t('Panel.Columns.Art'),
        t('Panel.Columns.Status'),
        t('Panel.Columns.Action'),
      ]}
    >
      {ROUNDS.map((kind) => {
        const retrieval = retrievals.find((r) => r.nachversandKind === kind);
        return (
          <Table.Tr key={kind}>
            <Table.Td fw="500">
              {kind === NachversandKind.BDM1 ? 'BDM 1' : 'BDM 2'}
            </Table.Td>
            <Table.Td>{ergebnis(kind, retrieval)}</Table.Td>
            {/* Versand oder Abholung: weicht nach einer Sperre bewusst vom Ergebnis ab —
                der Outcome bleibt Studiendatum, der Modus sagt, was wirklich passiert. */}
            <Table.Td>
              {retrieval ? (
                <Text size="14px">{t(MODE_KEY[retrieval.retrievalMode])}</Text>
              ) : (
                <Text size="12px" c="dimmed">
                  –
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              <StatusCell retrieval={retrieval} />
            </Table.Td>
            <Table.Td>
              {kind === NachversandKind.BDM1 &&
              bdmState?.bdm1Outcome !== Bdm1Outcome.VOUCHER ? (
                (() => {
                  const eligibility = bdm1SwitchEligibility(
                    retrieval,
                    bdmState,
                  );
                  const note = switchNoteLabel(t, eligibility);
                  return (
                    <Group gap="10px" align="flex-start" wrap="nowrap">
                      <Stack gap="6px" align="flex-start">
                        <ActionButton
                          retrieval={retrieval}
                          onAction={onAction}
                        />
                        <Button
                          size="xs"
                          variant="outline"
                          disabled={eligibility.blockReason !== null}
                          onClick={onSwitchToVoucher}
                        >
                          {t('Panel.Actions.SwitchToVoucher')}
                        </Button>
                      </Stack>
                      {note && (
                        <Text size="12px" c="dimmed" maw={200}>
                          {note}
                        </Text>
                      )}
                    </Group>
                  );
                })()
              ) : (
                <ActionButton retrieval={retrieval} onAction={onAction} />
              )}
            </Table.Td>
          </Table.Tr>
        );
      })}
    </TitledTable>
  );
};
